/**
 * Prompt Director. Uses Gemini (preferred) or OpenAI to:
 *   1) rewrite the user's intent into a director's brief
 *   2) recommend the best route (provider + model) from the capability matrix
 *
 * Falls back to a deterministic local rule set when no LLM key is available.
 */
import { DIRECTOR_PERSONA } from '../lib/persona';
import { buildAnimeAgentPrompt } from '../lib/anime-docs';
import { getAppContextMarkdown } from '../lib/context';
import { ALL_MODELS } from '../providers';
import { proxy } from '../providers/common';
import { getKey } from '../lib/keys';
import { UNIBLOCK_AGENT_BRIEF } from '../providers/uniblock';
import type { GenerateRequest, Mode } from './types';
import type { Provider } from '../lib/db';

export interface DirectorDecision {
  brief: string;
  negative: string;
  recommended: { provider: Provider; modelId: string; reason: string };
  alternates: { provider: Provider; modelId: string; reason: string }[];
}

function ruleBased(req: GenerateRequest): DirectorDecision {
  if (req.mode === 'sound') {
    return {
      brief: req.prompt,
      negative: req.negativePrompt ?? '',
      recommended: { provider: 'elevenlabs', modelId: 'elevenlabs-sound', reason: 'High-fidelity audio foley synthesizer' },
      alternates: []
    };
  }
  if (req.mode === '3d') {
    return {
      brief: req.prompt,
      negative: req.negativePrompt ?? '',
      recommended: { provider: 'meshy', modelId: 'meshy-3d', reason: 'Stereo isometric mesh & glTF synthesis' },
      alternates: []
    };
  }

  const wantVideo = req.mode === 'video';
  const refs = req.refs?.length ?? 0;
  const hasStartEnd = !!req.startFrame || !!req.endFrame;
  const wantsTransparent = /transparent|alpha|cutout|cyclorama/i.test(req.prompt);
  const wantsObjectRemoval = /remove (the|a)? ?(object|person|character|item)/i.test(req.prompt);

  if (wantVideo) {
    if (wantsObjectRemoval) {
      return {
        brief: req.prompt,
        negative: req.negativePrompt ?? defaultNegative('video'),
        recommended: { provider: 'gemini', modelId: 'veo-3.1', reason: 'Object removal + scene reconstruction strength' },
        alternates: [
          { provider: 'byteplus', modelId: 'dreamina-seedance-2-0-260128', reason: 'True alpha for compositing' },
          { provider: 'xai',      modelId: 'grok-imagine-video',           reason: 'Fast iteration + native audio' }
        ]
      };
    }
    if (wantsTransparent || refs >= 3) {
      return {
        brief: req.prompt,
        negative: req.negativePrompt ?? defaultNegative('video'),
        recommended: { provider: 'byteplus', modelId: 'dreamina-seedance-2-0-260128', reason: 'Multimodal references, transparent BG, character consistency' },
        alternates: [
          { provider: 'xai',    modelId: 'grok-imagine-video', reason: 'Up to 7 refs + native audio' },
          { provider: 'gemini', modelId: 'veo-3.1',            reason: 'Scene physics + start/end frames' }
        ]
      };
    }
    if (hasStartEnd) {
      return {
        brief: req.prompt,
        negative: req.negativePrompt ?? defaultNegative('video'),
        recommended: { provider: 'gemini', modelId: 'veo-3.1', reason: 'Strong start/end frame support' },
        alternates: [
          { provider: 'byteplus', modelId: 'dreamina-seedance-2-0-260128', reason: 'Reference role tagging' }
        ]
      };
    }
    return {
      brief: req.prompt,
      negative: req.negativePrompt ?? defaultNegative('video'),
      recommended: { provider: 'byteplus', modelId: 'dreamina-seedance-2-0-260128', reason: 'Default video workhorse' },
      alternates: [
        { provider: 'gemini', modelId: 'veo-3.1', reason: 'Cinematic physics' },
        { provider: 'xai',    modelId: 'grok-imagine-video', reason: 'Fast + audio' }
      ]
    };
  }

  // Image mode
  if (refs > 0) {
    return {
      brief: req.prompt,
      negative: req.negativePrompt ?? defaultNegative('image'),
      recommended: { provider: 'gemini', modelId: 'gemini-3-pro-image-preview', reason: 'Nano Banana Pro for ref-driven edits' },
      alternates: [
        { provider: 'byteplus', modelId: 'seedream-5-0-260128', reason: 'Strong reference lock' },
        { provider: 'openai',   modelId: 'gpt-image-1',         reason: 'Best prompt comprehension for edits' }
      ]
    };
  }
  return {
    brief: req.prompt,
    negative: req.negativePrompt ?? defaultNegative('image'),
    recommended: { provider: 'gemini', modelId: 'imagen-4', reason: 'High-fidelity text-to-image' },
    alternates: [
      { provider: 'byteplus', modelId: 'seedream-5-0-260128', reason: 'Photoreal + style range' },
      { provider: 'xai',      modelId: 'grok-imagine',        reason: 'Fast iteration' }
    ]
  };
}

function defaultNegative(m: Mode): string {
  if (m === 'image') return 'low resolution, blurry, distorted faces, extra fingers, warped text, watermark, oversaturation';
  return 'flicker, temporal artifacts, distorted faces, extra limbs, low resolution, watermark, jitter, morphing hands';
}

const capabilitySummary = () => ALL_MODELS
  .map(m => `- ${m.provider}/${m.id} (${m.mode}) refs:${m.caps.multiRef ?? 0} start/end:${m.caps.startEndFrame ? 'y' : 'n'} audio:${m.caps.audio ? 'y' : 'n'} edit:${m.caps.edit ? 'y' : 'n'} alpha:${m.caps.transparentBg ? 'y' : 'n'} maxDur:${m.caps.maxDurationSec ?? '-'}s`)
  .join('\n');

export async function chatWithDirector(
  agent: 'gemini' | 'chatgpt' | 'grok' | 'claude',
  userMsgText: string,
  history: { role: 'user' | 'assistant' | 'system', content: string }[],
  systemOverride?: string
): Promise<string> {
  const geminiKey = (await getKey('gemini')) || undefined;
  const openaiKey = (await getKey('openai')) || undefined;
  const xaiKey = (await getKey('xai')) || undefined;
  const anthropicKey = (await getKey('anthropic')) || undefined;

  const sysPrompt = systemOverride ?? `${DIRECTOR_PERSONA}

You are the creative director of this workspace. Answer the user conversationally and helpfully.

${UNIBLOCK_AGENT_BRIEF}

${getAppContextMarkdown()}`;

  const attempts: { provider: string; model: string; key: string | undefined; endpoint: string; authStyle?: 'x-goog-api-key' | 'bearer' | 'x-api-key' }[] = [];

  if (agent === 'chatgpt') {
    attempts.push({ provider: 'openai', model: 'gpt-4o-mini', key: openaiKey, endpoint: 'https://api.openai.com/v1/chat/completions', authStyle: 'bearer' });
  } else if (agent === 'grok') {
    attempts.push({ provider: 'xai', model: 'grok-beta', key: xaiKey, endpoint: 'https://api.x.ai/v1/chat/completions', authStyle: 'bearer' });
  } else if (agent === 'claude') {
    attempts.push({ provider: 'anthropic', model: 'claude-3-5-sonnet-latest', key: anthropicKey, endpoint: 'https://api.anthropic.com/v1/messages', authStyle: 'x-api-key' });
  } else {
    attempts.push({ provider: 'gemini', model: 'gemini-2.5-flash', key: geminiKey, endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent', authStyle: 'x-goog-api-key' });
  }

  // Add fallbacks
  const rest: any[] = [
    { provider: 'gemini', model: 'gemini-2.5-flash', key: geminiKey, endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent', authStyle: 'x-goog-api-key' },
    { provider: 'openai', model: 'gpt-4o-mini', key: openaiKey, endpoint: 'https://api.openai.com/v1/chat/completions', authStyle: 'bearer' },
    { provider: 'xai', model: 'grok-beta', key: xaiKey, endpoint: 'https://api.x.ai/v1/chat/completions', authStyle: 'bearer' }
  ].filter(x => x.provider !== (agent === 'chatgpt' ? 'openai' : agent === 'grok' ? 'xai' : agent === 'claude' ? 'anthropic' : 'gemini'));
  attempts.push(...rest);

  for (const att of attempts) {
    if (!att.key) continue;
    try {
      if (att.provider === 'gemini') {
        const res = await proxy<any>({
          provider: 'gemini', endpoint: att.endpoint,
          body: {
            systemInstruction: { role: 'system', parts: [{ text: sysPrompt }] },
            contents: [...history, { role: 'user', content: userMsgText }].filter(m => m.role !== 'system').map(m => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: [{ text: m.content }] })),
          },
          apiKey: att.key, authStyle: 'x-goog-api-key'
        });
        const text = res.candidates?.[0]?.content?.parts?.[0]?.text;
        if (text) return text;
      } else if (att.provider === 'anthropic') {
        const res = await proxy<any>({
          provider: 'anthropic', endpoint: att.endpoint, method: 'POST',
          body: {
            model: att.model, max_tokens: 4096, system: sysPrompt,
            messages: [...history, { role: 'user', content: userMsgText }].filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content }))
          },
          apiKey: att.key, authStyle: 'x-api-key'
        });
        const text = res.content?.[0]?.text;
        if (text) return text;
      } else {
        const res = await proxy<any>({
          provider: att.provider as 'openai' | 'xai', endpoint: att.endpoint,
          body: {
            model: att.model,
            messages: [{ role: 'system', content: sysPrompt }, ...history, { role: 'user', content: userMsgText }].map(m => ({ role: m.role, content: m.content })),
          },
          apiKey: att.key, authStyle: 'bearer'
        });
        const text = res.choices?.[0]?.message?.content;
        if (text) return text;
      }
    } catch (e) { console.warn(`Director Chat LLM ${att.provider} failed`, e); }
  }
  throw new Error("Chat generation failed: ensure API keys are configured and valid.");
}

/** Try LLM director; fall back to rules if no key or the LLM errors out. */
export async function decide(req: GenerateRequest): Promise<DirectorDecision> {
  const baseline = ruleBased(req);

  const geminiKey = (await getKey('gemini')) || undefined;
  const openaiKey = (await getKey('openai')) || undefined;
  const xaiKey = (await getKey('xai')) || undefined;
  if (!geminiKey && !openaiKey && !xaiKey) return baseline;

  const sysPrompt = `${DIRECTOR_PERSONA}

You also act as a router. Capability matrix:
${capabilitySummary()}

${buildAnimeAgentPrompt()}

${getAppContextMarkdown()}

Return STRICT JSON with shape:
{
  "brief": "director's brief paragraph",
  "negative": "negative prompt",
  "recommended": { "provider": "...", "modelId": "...", "reason": "..." },
  "alternates": [ { "provider": "...", "modelId": "...", "reason": "..." } ]
}`;

  const userMsg = `Mode: ${req.mode}
Prompt: ${req.prompt}
Aspect: ${req.aspect ?? 'auto'}
Refs: ${req.refs?.length ?? 0} | StartFrame: ${req.startFrame ? 'yes' : 'no'} | EndFrame: ${req.endFrame ? 'yes' : 'no'}
Duration: ${req.durationSec ?? '-'}s | Audio: ${req.refAudio?.length ? 'yes' : 'no'}`;

  // Prioritize provider depending on selected directorAgent
  const preferredAgent = req.directorAgent ?? 'gemini';
  const attempts: { provider: string; model: string; key: string | undefined; endpoint: string; authStyle?: 'x-goog-api-key' | 'bearer' }[] = [];

  if (preferredAgent === 'chatgpt') {
    attempts.push({ provider: 'openai', model: 'gpt-4o-mini', key: openaiKey, endpoint: 'https://api.openai.com/v1/chat/completions', authStyle: 'bearer' });
  } else if (preferredAgent === 'grok') {
    attempts.push({ provider: 'xai', model: 'grok-beta', key: xaiKey, endpoint: 'https://api.x.ai/v1/chat/completions', authStyle: 'bearer' });
  } else {
    attempts.push({ provider: 'gemini', model: 'gemini-2.5-flash', key: geminiKey, endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent', authStyle: 'x-goog-api-key' });
  }

  // Fallbacks
  const rest: { provider: string; model: string; key: string | undefined; endpoint: string; authStyle?: 'x-goog-api-key' | 'bearer' }[] = [
    { provider: 'gemini', model: 'gemini-2.5-flash', key: geminiKey, endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent', authStyle: 'x-goog-api-key' as const },
    { provider: 'openai', model: 'gpt-4o-mini', key: openaiKey, endpoint: 'https://api.openai.com/v1/chat/completions', authStyle: 'bearer' as const },
    { provider: 'xai', model: 'grok-beta', key: xaiKey, endpoint: 'https://api.x.ai/v1/chat/completions', authStyle: 'bearer' as const }
  ].filter(x => x.provider !== (preferredAgent === 'chatgpt' ? 'openai' : preferredAgent === 'grok' ? 'xai' : preferredAgent));

  attempts.push(...rest);

  for (const att of attempts) {
    if (!att.key) continue;
    try {
      if (att.provider === 'gemini') {
        const res = await proxy<any>({
          provider: 'gemini',
          endpoint: att.endpoint,
          body: {
            systemInstruction: { role: 'system', parts: [{ text: sysPrompt }] },
            contents: [{ role: 'user', parts: [{ text: userMsg }] }],
            generationConfig: { responseMimeType: 'application/json', temperature: 0.4 }
          },
          apiKey: att.key,
          authStyle: 'x-goog-api-key'
        });
        const text = res.candidates?.[0]?.content?.parts?.[0]?.text;
        if (text) return safeParse(text, baseline);
      } else {
        const res = await proxy<any>({
          provider: att.provider as any,
          endpoint: att.endpoint,
          body: {
            model: att.model,
            messages: [
              { role: 'system', content: sysPrompt },
              { role: 'user', content: userMsg }
            ],
            response_format: { type: 'json_object' },
            temperature: 0.4
          },
          apiKey: att.key,
          authStyle: att.authStyle
        });
        const text = res.choices?.[0]?.message?.content;
        if (text) return safeParse(text, baseline);
      }
    } catch (e) {
      console.warn(`Director LLM ${att.provider} failed, trying next fallback`, e);
    }
  }
  return baseline;
}

function safeParse(text: string, fallback: DirectorDecision): DirectorDecision {
  try {
    const j = JSON.parse(text);
    if (!j.recommended?.modelId) return fallback;
    return j as DirectorDecision;
  } catch { return fallback; }
}
